import { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { ConfirmDialog, ErrorBlock, LoadingBlock } from '@/components/activities/ActivityUi';
import { useDeleteVisit, useParticipantVisits } from '@/hooks/useVisits';
import { formatSessionDate } from '@/lib/activityTime';
import { formatVisitTimes, type StaffVisit } from '@/lib/visitForm';
import VisitModal from './VisitModal';

/** How many visits show before "Mostrar todas". */
const VISIBLE_VISITS = 10;

interface VisitsSectionProps {
  participantId: number;
  participantName: string;
  defaultCenterId: number | null;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

function byLine(visit: StaffVisit): string | null {
  const parts = [
    visit.checkedInByName && `entrada por ${visit.checkedInByName}`,
    visit.checkedOutByName && `saída por ${visit.checkedOutByName}`,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * A participant's centre visits, for staff to correct: record one nobody
 * checked in, fix the times, or remove one that did not happen.
 */
export default function VisitsSection({
  participantId,
  participantName,
  defaultCenterId,
  onSuccess,
  onError,
}: VisitsSectionProps) {
  const visitsQuery = useParticipantVisits(participantId);
  const deleteVisit = useDeleteVisit(participantId);
  const [showAll, setShowAll] = useState(false);
  const [editing, setEditing] = useState<StaffVisit | 'new' | null>(null);
  const [deleting, setDeleting] = useState<StaffVisit | null>(null);

  const visits = visitsQuery.data?.visits ?? [];
  const shown = showAll ? visits : visits.slice(0, VISIBLE_VISITS);

  const handleDelete = () => {
    if (!deleting) return;
    deleteVisit.mutate(deleting.id, {
      onSuccess: () => {
        onSuccess('Visita eliminada.');
        setDeleting(null);
      },
      onError: () => onError('Não foi possível eliminar a visita.'),
    });
  };

  return (
    <section aria-labelledby="visitas-heading" className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 id="visitas-heading" className="text-lg font-semibold text-foreground">
          Visitas ao centro
        </h2>
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Registar visita
        </button>
      </div>

      {visitsQuery.isLoading && <LoadingBlock />}
      {visitsQuery.isError && <ErrorBlock message="Não foi possível carregar as visitas." />}
      {visitsQuery.data && visits.length === 0 && (
        <div className="card py-10 text-center">
          <p className="text-sm text-muted-foreground">{participantName} ainda não tem visitas registadas.</p>
        </div>
      )}

      {shown.length > 0 && (
        <ul className="card divide-y divide-border">
          {shown.map((visit) => {
            const by = byLine(visit);
            const when = `${formatSessionDate(visit.checkedInAt)}, ${formatVisitTimes(visit)}`;
            return (
              <li key={visit.id} className="flex items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground">{when}</p>
                  <p className="text-xs text-muted-foreground">
                    {[visit.digitalCenterName, by].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditing(visit)}
                  aria-label={`Corrigir a visita de ${when}`}
                  title="Corrigir"
                  className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleting(visit)}
                  aria-label={`Eliminar a visita de ${when}`}
                  title="Eliminar"
                  className="p-2 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {!showAll && visits.length > VISIBLE_VISITS && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="text-sm font-medium text-primary hover:underline"
        >
          Mostrar todas ({visits.length})
        </button>
      )}

      {editing && (
        <VisitModal
          participantId={participantId}
          participantName={participantName}
          visit={editing === 'new' ? undefined : editing}
          defaultCenterId={defaultCenterId}
          onClose={() => setEditing(null)}
          onSuccess={onSuccess}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Eliminar esta visita?"
          confirmLabel="Eliminar"
          isPending={deleteVisit.isPending}
          onConfirm={handleDelete}
          onClose={() => setDeleting(null)}
        >
          A visita de {formatSessionDate(deleting.checkedInAt)}, {formatVisitTimes(deleting)}, em{' '}
          {deleting.digitalCenterName}, deixa de contar no percurso e nos relatórios.
        </ConfirmDialog>
      )}
    </section>
  );
}
